
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import Clock from "../../common/components/Clock"
import CustomForm from "../../common/components/CustomForm"
import Icon from "../../common/components/Icon"
import useClientConfig from "../../common/hooks/clientConfigHook"
import { cdate } from "../../common/utils/CDate"
import { safeEval } from "../../common/utils/safeEval"
import PropTypes from 'prop-types';
import { useSelectedPCR } from "../../utils/CADStateProvider"
import { buildColor } from "../../common/utils/tableUtilities"
import JobIDLabel from "../JobIDLabel"
import { useReadonlyEpcr } from "../../reducers/epcrSlice"
import { useNetworkState } from "../../common/hooks/appStateHook"

export const EpcrListEntry = ({epcr, disabled = false, onClick}) => {
    const clientConfig = useClientConfig();
    const { selectedPcrID } = useSelectedPCR();
    const { t } = useTranslation();
    const fullEpcr = useReadonlyEpcr(epcr.id);
    const { wsStatus } = useNetworkState();

    const elapsedTime = useMemo(() => epcr.finalized ? cdate(epcr.creation, clientConfig.tz).duration(cdate(epcr.finalized, clientConfig.tz)) : null, [epcr.finalized, epcr.creation, clientConfig.tz]);
    
    const renderLocation = useMemo(() => clientConfig?.epcrGetLocation ? safeEval(clientConfig.epcrGetLocation) : () => null, [clientConfig?.epcrGetLocation]);

    const clickable = useMemo(() => !disabled && onClick && (wsStatus === 'authenticated' || fullEpcr), [disabled, onClick, wsStatus, fullEpcr]);

    let handleClick = () => {
        if (clickable) {
            onClick();
        }
    }

    const getPriorityColor = (field) => {
        field = field ?? "color";
        let acuity = epcr.finalpatientacuity ?? epcr.initialpatientacuity;
        if (!acuity) {
          return null;
        }
        let priority = clientConfig.patientAcuities.find(a => a.value == acuity);
        return priority?.[field];
      }

    //#region list components
    const ids = <><div className="d-flex flex-row justify-content-between">
        <span className="pl-2" style={{ fontWeight: 'bold' }}>{t('pcrNumber')}: {epcr.displayid}</span>
        <span className="pr-2 pt-2" >{epcr.finalized ? <Icon intent="locked"></Icon> : null}</span>
    </div>
    {epcr.jobs?.length > 0 ?
        <div className="d-flex">
            <div className="pl-2">{t('jobNumber')}:</div>
            <div className="d-flex flex-column">{epcr.jobs.map(j => <div key={j}><JobIDLabel jobid={j}></JobIDLabel></div>)}</div>
        </div> : null
    }</>

    const priority = (epcr.finalpatientacuity || epcr.initialpatientacuity) ? 
        <div style={{ backgroundColor: getPriorityColor() }} className="d-flex align-items-center flex-column justify-content-around h-100">
            <div style={{ color: getPriorityColor('textColor') }}>{epcr.finalpatientacuity ?? epcr.initialpatientacuity}</div>
            <div style={{ color: getPriorityColor('textColor') }}>{t('priority', { count: 1 })}</div>
        </div> :
        null;

    const allergies = epcr.has_allergies? 
        <div style={{ backgroundColor: 'orange' }} className="d-flex align-items-center flex-column justify-content-around h-100">
            <div><Icon variant="solid" kind="exclamation"></Icon></div>
            <div>{t('allergy', { count: 1 })}</div>
        </div> :
        null;

    const medications = epcr.medications_given ? 
        <div style={{ backgroundColor: 'orange' }} className="d-flex align-items-center flex-column justify-content-around h-100">
            <div><Icon variant="solid" kind="syringe"></Icon></div>
            <div>{t('med', { count: 1 })}</div>
        </div> :
        null

    const time = <div className="d-flex justify-content-center align-items-center h-100">
        {/* TODO this may need to be formatted differently for different locales */}
        {epcr.finalized ? (<div>{('00' + elapsedTime.hours()).slice(-2)}:{('00' + elapsedTime.minutes()).slice(-2)}:{('00' + elapsedTime.seconds()).slice(-2)}{elapsedTime.days() > 0 ? '+' + elapsedTime.days() : ''}</div>)
        : (<Clock baseDate={epcr.creation}></Clock>)}
    </div>

    const custom = clientConfig?.epcrListFormSpec ? <CustomForm def={clientConfig.epcrListFormSpec} data={epcr ?? {}} readonly={true} hideLabel={true}></CustomForm> : null
    //#endregion

    const buttonColor = buildColor('#ffffff', selectedPcrID?.toString() === epcr.id?.toString());


    return <div className="pb-3" style={{ opacity: clickable ? 1 : 0.7 }} ><div className="d-none d-md-block">
        <button type="button" className="w-100 d-block" style={{ border: 'none', padding: 0, textAlign: 'initial', borderRadius: '8px', ...buttonColor }} onClick={handleClick}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(14, 1fr)" }}>
            {/* Row 1: PCR/Case numbers, priority, allergies, medications, elapsed time */}
            <div style={{ gridColumn: "1/5", borderStartStartRadius: '8px', backgroundColor: '#f7f7f7' }} className="border border-dark border-right-0">{ids}</div>
            <div style={{ gridColumn: "5/7", backgroundColor: '#f7f7f7' }} className="border border-dark border-left-0 border-right-0">{priority}</div>
            <div style={{ gridColumn: "7/9", backgroundColor: '#f7f7f7' }} className="border border-dark border-left-0 border-right-0">{allergies}</div>
            <div style={{ gridColumn: "9/11", backgroundColor: '#f7f7f7' }} className="border border-dark border-left-0 border-right-0">{medications}</div>
            <div style={{ gridColumn: "11/15", backgroundColor: '#f7f7f7', borderStartEndRadius: '8px' }} className="border border-left-0 border-dark">{time}</div>
            {/* Row 2: Patient details, primary complaint and timestamps (custom form) */}
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-right-0">{t('firstname')}:</div>
            <div style={{ gridColumn: "4/8" }} className="pl-2 border border-dark border-top-0 border-right-0">{epcr.firstname}</div>
            <div style={{ gridColumn: "8/15" }} className="border border-dark border-top-0 d-flex justify-content-between">
                <div className="pl-2">{t('chiefComplaint')}:</div>
                <div className="pr-2">{epcr.problem}</div>
            </div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-right-0">{t('lastname')}:</div>
            <div style={{ gridColumn: "4/8" }} className="pl-2 border border-dark border-top-0 border-right-0">{epcr.lastname}</div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-right-0">{t('dob')}:</div>
            <div style={{ gridColumn: "4/8" }} className="pl-2 border border-dark border-top-0 border-right-0">{cdate(epcr.dob, 'utc')?.dateFormat()}</div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-right-0">{t('gender')}:</div>
            <div style={{ gridColumn: "4/8" }} className="pl-2 border border-dark border-top-0 border-right-0">{epcr.gender}</div>
            <div style={{ gridColumn: "8/15", gridRow: "3/6" }} className="border border-dark border-top-0">{custom}</div>
            {/* Row 3: Location */}
            <div style={{ gridColumn: "1/15", borderEndStartRadius: '8px', borderEndEndRadius: '8px' }} className="pl-2 border border-dark border-top-0">{t('location', { count: 1 })}: {renderLocation({ customfields: {}, ...epcr })}</div>
        </div>
        </button>
    </div>
    <div className="d-md-none">
        <button type="button" className="w-100 d-block text-dark bg-light" style={{ border: 'none', padding: 0, textAlign: 'initial', ...buttonColor }} onClick={handleClick}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)" }} onClick={handleClick}>
            {/* PCR/Case numbers, priority, allergies, medications, elapsed time */}
            <div style={{ gridColumn: "1/7" }} className="border border-dark">{ids}</div>
            <div style={{ gridColumn: "1/3" }} className="border border-dark border-top-0 border-right-0">{priority}</div>
            <div style={{ gridColumn: "3/5" }} className="border border-dark border-top-0 border-right-0">{allergies}</div>
            <div style={{ gridColumn: "5/7" }} className="border border-dark border-top-0">{medications}</div>
            <div style={{ gridColumn: "1/7" }} className="border border-dark border-top-0">{time}</div>
            {/* Patient details */}
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-bottom-0 border-right-0">{t('firstname')}:</div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-right-0">{epcr.firstname}</div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-bottom-0 border-right-0">{t('lastname')}:</div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-right-0">{epcr.lastname}</div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-bottom-0 border-right-0">{t('dob')}:</div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-right-0">{cdate(epcr.dob, 'utc')?.dateFormat()}</div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-bottom-0 border-right-0">{t('gender')}:</div>
            <div style={{ gridColumn: "1/4" }} className="pl-2 border border-dark border-top-0 border-right-0">{epcr.gender}</div>
            {/* Cheif complaint */}
            <div style={{ gridColumn: "4/7", gridRow: "4/6"}} className="border border-dark border-top-0">
                <div className="pl-2">{t('chiefComplaint')}:</div>
                <div className="pl-2">{epcr.problem}</div>
            </div>
            {/* Timestamps  (custom form)*/}
            <div style={{ gridColumn: "4/7", gridRow: "6/12" }} className="border border-dark border-top-0">{custom}</div>
            {/* location */}
            <div style={{ gridColumn: "1/7" }} className="pl-2 border border-dark border-top-0">{t('location', { count: 1 })}: {renderLocation({ customfields: {}, ...epcr })}</div>
        </div>
        </button>
    </div></div>
}

EpcrListEntry.propTypes = {
    epcr: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    onClick: PropTypes.func
}